import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Link, Paper,
    Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { apiErrorMessage, hrService, HRReimbursement } from '../../services/hrService';
import { openUploadedFile } from '../../services/fileService';
import { formatCurrency } from '../../utils/format';
import { formatDateBR } from '../../utils/date';
import { REIMBURSEMENT_STATUS_COLORS, REIMBURSEMENT_STATUS_LABELS, ReimbursementStatus } from '../../types/collaborator';

const TABS: { label: string; status?: ReimbursementStatus }[] = [
    { label: 'Pendentes', status: 'PENDING' },
    { label: 'Aprovados (a pagar)', status: 'APPROVED' },
    { label: 'Pagos', status: 'PAID' },
    { label: 'Recusados', status: 'REJECTED' },
    { label: 'Todos' },
];

const AprovacoesList: React.FC = () => {
    const { user } = useAuth();
    const [tab, setTab] = useState(0);
    const [items, setItems] = useState<HRReimbursement[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [decision, setDecision] = useState<{ item: HRReimbursement; status: ReimbursementStatus } | null>(null);
    const [notes, setNotes] = useState('');
    const [saving, setSaving] = useState(false);
    const [decisionError, setDecisionError] = useState<string | null>(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            setItems(await hrService.listReimbursements(TABS[tab].status));
            setError(null);
        } catch (err) {
            setError(apiErrorMessage(err, 'Erro ao carregar os reembolsos.'));
        } finally {
            setLoading(false);
        }
    }, [tab]);

    useEffect(() => { load(); }, [load]);

    const openDecision = (item: HRReimbursement, status: ReimbursementStatus) => {
        setNotes('');
        setDecisionError(null);
        setDecision({ item, status });
    };

    const confirm = async () => {
        if (!decision) return;
        try {
            setSaving(true);
            await hrService.decideReimbursement(decision.item.id, decision.status, notes);
            setSuccess(`Reembolso de ${decision.item.user_name} marcado como ${REIMBURSEMENT_STATUS_LABELS[decision.status].toLowerCase()}.`);
            setDecision(null);
            load();
        } catch (err) {
            setDecisionError(apiErrorMessage(err, 'Erro ao registrar a decisão.'));
        } finally {
            setSaving(false);
        }
    };

    const total = items.reduce((sum, i) => sum + i.amount, 0);

    return (
        <Box>
            <Typography variant="h5" gutterBottom>Aprovações de reembolso</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

            <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" allowScrollButtonsMobile sx={{ mb: 2 }}>
                {TABS.map((t) => <Tab key={t.label} label={t.label} />)}
            </Tabs>

            {loading && <LinearProgress sx={{ mb: 1 }} />}
            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Colaborador</TableCell>
                            <TableCell>Data da despesa</TableCell>
                            <TableCell>Descrição</TableCell>
                            <TableCell align="right">Valor</TableCell>
                            <TableCell>Comprovante</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Ações</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {items.map((item) => {
                            const own = item.user_id === user?.id;
                            return (
                                <TableRow key={item.id} hover>
                                    <TableCell>{item.user_name}</TableCell>
                                    <TableCell>{formatDateBR(item.date)}</TableCell>
                                    <TableCell>
                                        {item.description}
                                        {item.review_notes && (
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                Parecer: {item.review_notes}
                                            </Typography>
                                        )}
                                    </TableCell>
                                    <TableCell align="right">{formatCurrency(item.amount)}</TableCell>
                                    <TableCell>
                                        {item.receipt_url ? (
                                            <Link component="button" type="button"
                                                onClick={() => openUploadedFile(item.receipt_url!).catch(() => setError('Não foi possível abrir o comprovante.'))}>
                                                Ver
                                            </Link>
                                        ) : <Typography variant="caption" color="text.secondary">Sem comprovante</Typography>}
                                    </TableCell>
                                    <TableCell>
                                        <Chip size="small" label={REIMBURSEMENT_STATUS_LABELS[item.status]} color={REIMBURSEMENT_STATUS_COLORS[item.status]} />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                                            {own && item.status === 'PENDING' && (
                                                <Typography variant="caption" color="text.secondary">Seu pedido: outra pessoa aprova</Typography>
                                            )}
                                            {!own && item.status === 'PENDING' && (
                                                <>
                                                    <Button size="small" variant="contained" color="success" onClick={() => openDecision(item, 'APPROVED')}>Aprovar</Button>
                                                    <Button size="small" color="error" onClick={() => openDecision(item, 'REJECTED')}>Recusar</Button>
                                                </>
                                            )}
                                            {!own && item.status === 'APPROVED' && (
                                                <>
                                                    <Button size="small" variant="contained" onClick={() => openDecision(item, 'PAID')}>Marcar como pago</Button>
                                                    <Button size="small" color="error" onClick={() => openDecision(item, 'REJECTED')}>Recusar</Button>
                                                </>
                                            )}
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                        {!loading && items.length === 0 && (
                            <TableRow><TableCell colSpan={7} align="center">Nenhum reembolso nesta situação.</TableCell></TableRow>
                        )}
                        {items.length > 0 && (
                            <TableRow>
                                <TableCell colSpan={3}><b>Total</b></TableCell>
                                <TableCell align="right"><b>{formatCurrency(total)}</b></TableCell>
                                <TableCell colSpan={3} />
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog open={!!decision} onClose={() => setDecision(null)} maxWidth="sm" fullWidth>
                <DialogTitle>
                    {decision?.status === 'APPROVED' ? 'Aprovar reembolso' : decision?.status === 'PAID' ? 'Confirmar pagamento' : 'Recusar reembolso'}
                </DialogTitle>
                <DialogContent>
                    {decision && (
                        <Typography gutterBottom>
                            {decision.item.user_name} — {decision.item.description} — <b>{formatCurrency(decision.item.amount)}</b>
                        </Typography>
                    )}
                    {decisionError && <Alert severity="error" sx={{ my: 1 }}>{decisionError}</Alert>}
                    <TextField fullWidth multiline minRows={3} margin="normal" value={notes} onChange={(e) => setNotes(e.target.value)}
                        label={decision?.status === 'REJECTED' ? 'Motivo da recusa (obrigatório)' : 'Observação (opcional)'}
                        helperText="O colaborador vê este parecer na tela de reembolsos" />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDecision(null)}>Cancelar</Button>
                    <Button variant="contained" color={decision?.status === 'REJECTED' ? 'error' : 'primary'} onClick={confirm}
                        disabled={saving || (decision?.status === 'REJECTED' && !notes.trim())}>
                        Confirmar
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AprovacoesList;
